import { createDetailedValidator } from 'typecheck.macro';
import { logErrorToSentry } from '../../Sentry';
import { Fetch } from '../../services/NetworkService';
import { asDynamic } from '../../util/Dynamic';
import { parse, parseFromFetchResponse } from '../../util/parse';
import { AlgoliaIndex } from './AlgoliaIndex';
import { ALGOLIA_API_KEY, ALGOLIA_APPLICATION_ID } from './constants';

/** Fetches results from the given Algolia index.
 * @returns an array of parsed results, excluding any which did not parse correctly.
 *    Logs to sentry upon seeing invalid data.
 * @throws when the fetch to Algolia fails altogether, or does not return the expected response shape.
 */
export const fetchHits = async <T>(fetch: Fetch, input: string, index: AlgoliaIndex<T>) => {
  const response = await fetch(makeApiUrl(index.name), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      params: `query=${input}&hitsPerPage=5`,
    }),
  });

  const json = await parseFromFetchResponse(response, AlgoliaResponseValidator);

  const parsedHits = json.hits.flatMap(hit => {
    try {
      return [parse(hit, index.validator)];
    } catch (cause) {
      logErrorToSentry(`Failed to parse data in index "${index.name}"`, {
        cause: asDynamic(cause),
      });

      return [];
    }
  });

  return parsedHits;
};

const makeApiUrl = (db: string) =>
  `https://lksw35ekdj-dsn.algolia.net/1/indexes/${db}/query?x-algolia-agent=Algolia%20for%20vanilla%20JavaScript%203.24.4&x-algolia-application-id=${ALGOLIA_APPLICATION_ID}-algolia-api-key=${ALGOLIA_API_KEY}`;

const AlgoliaResponseValidator = createDetailedValidator<{ hits: unknown[] }>();
